import { useState, useMemo } from 'react'
import {
  BadgeDollarSign,
  Calendar,
  Target,
  MousePointerClick,
  Eye,
  Percent,
  Search,
  Zap,
  History,
  MessageCircle,
} from "lucide-react"

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { Input } from "@/components/ui/input"

interface Metrics {
  spend: number
  conversions: number
  clicks: number
  impressions: number
}

interface SmartSearchProps {
  metrics: {
    [platform: string]: Metrics
  }
  streakData?: {
    currentStreak: number
    bestStreak: number
    totalDays: number
  }
}

export function SmartSearch({ metrics, streakData }: SmartSearchProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  // Calcula métricas totais
  const totalMetrics = Object.values(metrics).reduce(
    (acc, platformMetrics) => ({
      spend: acc.spend + (platformMetrics?.spend || 0),
      conversions: acc.conversions + (platformMetrics?.conversions || 0),
      clicks: acc.clicks + (platformMetrics?.clicks || 0),
      impressions: acc.impressions + (platformMetrics?.impressions || 0),
    }),
    { spend: 0, conversions: 0, clicks: 0, impressions: 0 }
  )

  const cac = totalMetrics.conversions > 0 ? totalMetrics.spend / totalMetrics.conversions : 0
  const ctr = totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0

  // Lista de todas as métricas disponíveis com suas palavras-chave
  const metricsKeywords = useMemo(() => [
    {
      keywords: ['investimento', 'gasto', 'custo', 'total', 'spend', 'dinheiro', 'valor'],
      value: totalMetrics.spend,
      icon: BadgeDollarSign,
      label: 'Total Investido',
      format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    },
    {
      keywords: ['conversões', 'conversao', 'conversoes', 'vendas', 'leads'],
      value: totalMetrics.conversions,
      icon: Target,
      label: 'Conversões',
      format: (value: number) => value.toLocaleString(),
    },
    {
      keywords: ['cliques', 'clicks', 'interações', 'interacao'],
      value: totalMetrics.clicks,
      icon: MousePointerClick,
      label: 'Cliques',
      format: (value: number) => value.toLocaleString(),
    },
    {
      keywords: ['impressões', 'impressoes', 'visualizações', 'views'],
      value: totalMetrics.impressions,
      icon: Eye,
      label: 'Impressões',
      format: (value: number) => value.toLocaleString(),
    },
    {
      keywords: ['ctr', 'taxa de clique', 'taxa'],
      value: ctr,
      icon: Percent,
      label: 'CTR',
      format: (value: number) => `${value.toFixed(2)}%`,
    },
    {
      keywords: ['cac', 'custo por conversão', 'custo por venda', 'custo aquisição'],
      value: cac,
      icon: BadgeDollarSign,
      label: 'CAC',
      format: (value: number) => `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
    },
  ], [totalMetrics, cac, ctr])

  // Lista de métricas de streak com palavras-chave
  const streakKeywords = useMemo(() => streakData ? [
    {
      keywords: ['streak atual', 'sequência atual', 'dias seguidos'],
      value: streakData.currentStreak,
      icon: Zap,
      label: 'Streak Atual',
      format: (value: number) => `${value} dias`,
    },
    {
      keywords: ['melhor streak', 'maior sequência', 'recorde'],
      value: streakData.bestStreak,
      icon: History,
      label: 'Melhor Streak',
      format: (value: number) => `${value} dias`,
    },
    {
      keywords: ['total dias', 'dias totais', 'total de dias'],
      value: streakData.totalDays,
      icon: Calendar,
      label: 'Total de Dias',
      format: (value: number) => `${value} dias`,
    },
  ] : [], [streakData])

  // Filtra as métricas com base na pesquisa
  const filteredMetrics = useMemo(() => {
    if (!searchQuery) return metricsKeywords

    const query = searchQuery.toLowerCase()
    return metricsKeywords.filter(metric =>
      metric.keywords.some(keyword => keyword.includes(query)) ||
      metric.label.toLowerCase().includes(query)
    )
  }, [searchQuery, metricsKeywords])

  // Filtra as métricas de streak com base na pesquisa
  const filteredStreaks = useMemo(() => {
    if (!searchQuery || !streakData) return streakKeywords

    const query = searchQuery.toLowerCase()
    return streakKeywords.filter(streak =>
      streak.keywords.some(keyword => keyword.includes(query)) ||
      streak.label.toLowerCase().includes(query)
    )
  }, [searchQuery, streakKeywords, streakData])

  const handleSubmit = async (query: string) => {
    if (!query.trim() || isLoading) return

    setIsLoading(true)
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      
      if (!user.token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente.')
      }

      const response = await fetch('https://waffle-v2.thenewcc.workers.dev/insights/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({ message: query })
      })

      if (!response.ok) {
        throw new Error(
          response.status === 401 
            ? 'Sessão expirada. Por favor, faça login novamente.' 
            : 'Erro ao processar sua mensagem. Tente novamente.'
        )
      }

      const data = await response.json()
      
      if (!data.response || typeof data.response !== 'string') {
        throw new Error('Desculpe, ainda não estou preparado para esse tipo de pergunta.')
      }

      toast(data.response, {
        position: "bottom-right",
        className: "max-w-[600px] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-lg",
        duration: 8000,
        style: {
          marginTop: '60px',
          padding: '16px',
          borderRadius: '8px',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          fontSize: '14px',
          lineHeight: '1.5',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        },
        description: (
          <div className="mt-2 flex items-center">
            <History className="h-4 w-4 mr-1 text-gray-500" />
            <span className="text-xs text-gray-500">Resposta gerada com base nos dados de APIs</span>
          </div>
        )
      })
    } catch (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : 'Ocorreu um erro inesperado. Por favor, tente novamente.'

      toast.error(errorMessage, {
        position: "top-center",
        className: "max-w-[600px]",
        duration: 5000,
        style: {
          marginTop: '60px'
        }
      })

      if (errorMessage.includes('Sessão expirada')) {
        localStorage.removeItem('user')
        window.location.reload()
      }
    } finally {
      setIsLoading(false)
      setOpen(false)
      setSearchQuery('')
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          role="combobox" 
          aria-expanded={open}
          className="w-full justify-start text-muted-foreground"
        >
          <Search className="mr-2 h-4 w-4" />
          <span className="text-sm text-muted-foreground">
            Digite sua pergunta sobre métricas ou streaks...
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[600px] p-0" align="start">
        <Command shouldFilter={false} className="border-none">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-0 focus-visible:ring-0"
              placeholder="Digite sua pergunta sobre métricas ou streaks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchQuery.trim() && 
                    filteredMetrics.length === 0 && filteredStreaks.length === 0) {
                  handleSubmit(searchQuery)
                }
              }}
            />
          </div>
          <CommandList>
            <CommandEmpty>
              {isLoading ? (
                <div className="flex items-center justify-center p-4 gap-2">
                  <div className="w-2 h-2 bg-[#FF3366] rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-[#FF3366] rounded-full animate-bounce delay-100" />
                  <div className="w-2 h-2 bg-[#FF3366] rounded-full animate-bounce delay-200" />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center p-4 gap-2">
                  <MessageCircle className="h-8 w-8 text-[#FF3366]" />
                  <p className="text-sm text-center text-muted-foreground">
                    Nenhum resultado encontrado. Pressione Enter para perguntar ao waffle chat.
                  </p>
                </div>
              )}
            </CommandEmpty>
            
            {filteredMetrics.length > 0 && (
              <CommandGroup heading="Métricas de Marketing">
                {filteredMetrics.map((metric, index) => (
                  <CommandItem 
                    key={index} 
                    className="flex items-center gap-2"
                    onSelect={() => {
                      setSearchQuery('')
                      setOpen(false)
                    }}
                  >
                    <metric.icon className="h-4 w-4 text-[#FF3366]" />
                    <span>{metric.label}:</span>
                    <span className="font-bold">
                      {metric.format(metric.value)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {filteredStreaks.length > 0 && (
              <CommandGroup heading="Streaks">
                {filteredStreaks.map((streak, index) => (
                  <CommandItem 
                    key={index} 
                    className="flex items-center gap-2"
                    onSelect={() => {
                      setSearchQuery('')
                      setOpen(false)
                    }}
                  >
                    <streak.icon className="h-4 w-4 text-[#FF3366]" />
                    <span>{streak.label}:</span>
                    <span className="font-bold">
                      {streak.format(streak.value)}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
} 